<template>
    <div class="form-field">
        <div class="form-field__input">
            <label
                v-if="label"
                :for="id"
                class="form-field__label"
                :class="{ 'is-active': isLabelActive || password }"
            >
                {{ label }}
            </label>

            <div class="password-field">
                <input
                    :id="id"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    :placeholder="placeholder"
                    :class="['input-field', { 'input-field_error': hasError }, { 'has-label': label }]"
                    @focus="isLabelActive = true"
                    @blur="isLabelActive = !!password"
                />

                <span class="password-field__eye-icon" @click="togglePasswordVisibility">
                    <span v-if="showPassword">
                        <img
                            v-svg-inline
                            src="@/assets/icons/ui/password-input-eye-open.svg"
                        />
                    </span>
                    <span v-else>
                        <img
                            v-svg-inline
                            src="@/assets/icons/ui/password-input-eye-close.svg"
                        />
                    </span>
                </span>
            </div>
        </div>

        <span v-if="hasError" class="form-field__error">
            {{ errorMessage }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import { ref, watch, computed, defineProps, defineEmits } from 'vue';

    const props = defineProps({
        required: {
            type: Boolean,
            default: false,
        },
        label: String,
        placeholder: String,
        modelValue: {
            type: String,
            default: '',
        },
        id: String,
        errorMessage: String,
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string): void,
    }>();

    const showPassword = ref(false);
    const password = ref(props.modelValue || '');
    const isLabelActive = ref(!!props.modelValue);
    const hasError = computed(() => !!props.errorMessage);

    watch(
        () => props.modelValue,
        (newValue) => {
            if (newValue !== password.value) {
                password.value = newValue || '';
                isLabelActive.value = !!newValue;
            }
        },
    );

    watch(password, (newValue) => {
        emit('update:modelValue', newValue);
        isLabelActive.value = !!newValue;
    });

    function togglePasswordVisibility(): void {
        showPassword.value = !showPassword.value;
    }
</script>

<style lang="scss" scoped>
    @use './UIPasswordInput.scss' as *;
</style>
